import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import { useMemo } from "react";
import useSWR from "swr";

import { ApiRouteAttempt } from "../types";
import useVenueRoutes from "./useVenueRoutes";

export type Attempt = {
  uuid?: string;
  success: boolean;
  segmentsCount: number;
  timestamp: Date;
  usedLastHold?: boolean;
  duration?: number;
  notes?: string;
  judge?: string | null;
};

export const useAttempts = (routeUUID?: string) => {
  const {
    data: attempts,
    isValidating,
    error,
  } = useSWR<ApiRouteAttempt[]>(`/routes/${routeUUID}/attempts?user=ALL`);

  const attemptsByUser = useMemo(() => {
    return (
      attempts &&
      groupBy(
        sortBy(
          attempts,
          (attempt) => attempt.user,
          (attempt) => new Date(attempt.timestamp)
        ),
        (attempt) => attempt.user
      )
    );
  }, [attempts]);

  return {
    attemptsByUser: (attempts && attemptsByUser) ?? attemptsByUser,
    isValidating,
    error,
  };
};

export default function useUserAttempts({
  routeUUID,
  venueUUID,
  userUUID,
}: {
  routeUUID?: string;
  venueUUID?: string;
  userUUID?: string;
}) {
  const route = useVenueRoutes(venueUUID).routes?.find(
    (r) => r.uuid === routeUUID
  );

  const {
    data: attemptsData,
    isValidating: isAttemptsValidating,
    error: attemptsError,
  } = useSWR<ApiRouteAttempt[]>(
    `/routes/${routeUUID}/attempts?user=${userUUID}`
  );

  const attempts: Attempt[] | undefined = useMemo(
    () =>
      attemptsData
        ? sortBy(
            attemptsData?.map((attempt) => ({
              ...attempt,
              segmentsCount: attempt.segmentsCount || 0,
              timestamp: new Date(attempt.timestamp),
            })),
            "timestamp"
          )
        : undefined,
    [attemptsData]
  );

  return {
    route,
    attempts,
    isValidating: isAttemptsValidating,
    error: attemptsError,
  };
}
